import { LoginRequestSchema, RegisterRequestSchema } from '@swi/contracts';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { isApiError } from '../api/problem';
import { Brand } from '../components/layout/AppShell';
import { Button, ErrorNote, Field, Input } from '../components/ui/primitives';
import { useAuth } from '../lib/auth';

/**
 * Login and registration.
 *
 * The form is validated with the same schema the API validates the request
 * with, so the two can never disagree about what a valid payload is.
 */
const DEMO_ACCOUNTS: ReadonlyArray<readonly [string, string]> = [
  ['renter@demo.swi', 'nájemce — rezervuje a sleduje let'],
  ['owner@demo.swi', 'operátor flotily — schvaluje a dispatchuje'],
  ['admin@demo.swi', 'admin — správa uživatelů a hubů'],
];

export function LoginPage() {
  const { status, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFailure(null);

    const parsed =
      mode === 'login'
        ? LoginRequestSchema.safeParse({ email, password })
        : RegisterRequestSchema.safeParse({ email, password, displayName });

    if (!parsed.success) {
      const collected: Record<string, string[]> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.') || '_';
        collected[key] = [...(collected[key] ?? []), issue.message];
      }
      setErrors(collected);
      return;
    }

    setErrors({});
    setBusy(true);
    try {
      const user =
        mode === 'login'
          ? await login(email, password)
          : await register(email, password, displayName);
      toast.success(`Vítej, ${user.displayName}`);
      navigate('/', { replace: true });
    } catch (error) {
      if (isApiError(error)) {
        setFailure(error.humanMessage);
        setErrors(error.fieldErrors);
      } else {
        setFailure(error instanceof Error ? error.message : 'Přihlášení se nepovedlo');
      }
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (demoEmail: string) => {
    setMode('login');
    setEmail(demoEmail);
    setPassword('AirshipDemo2026!');
  };

  return (
    <div className="relative z-10 grid min-h-dvh place-items-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex justify-center">
          <Brand />
        </div>

        <form onSubmit={submit} className="glass flex flex-col gap-4 p-6">
          <div>
            <h1 className="text-lg font-semibold">
              {mode === 'login' ? 'Přihlášení' : 'Registrace'}
            </h1>
            <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
              {mode === 'login'
                ? 'Rezervace vzducholodí a sledování letů.'
                : 'Nový účet vznikne v roli nájemce.'}
            </p>
          </div>

          {failure !== null && <ErrorNote title={failure} />}

          {mode === 'register' && (
            <Field label="Jméno" error={errors.displayName?.[0]}>
              <Input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                autoComplete="name"
                placeholder="Jana Nájemcová"
                required
              />
            </Field>
          )}

          <Field label="E-mail" error={errors.email?.[0]}>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              placeholder="renter@demo.swi"
              required
            />
          </Field>

          <Field
            label="Heslo"
            error={errors.password?.[0]}
            hint={mode === 'register' ? 'Alespoň 8 znaků.' : undefined}
          >
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
            />
          </Field>

          <Button type="submit" variant="primary" loading={busy}>
            {mode === 'login' ? 'Přihlásit se' : 'Vytvořit účet'}
          </Button>

          <button
            type="button"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setErrors({});
              setFailure(null);
            }}
            className="text-[13px] text-[var(--text-secondary)] underline-offset-4 hover:underline"
          >
            {mode === 'login' ? 'Nemám účet — registrovat se' : 'Už mám účet — přihlásit se'}
          </button>
        </form>

        <div className="glass mt-4 p-4">
          <p className="label-caps mb-2">Demo účty</p>
          <div className="flex flex-col gap-1.5">
            {DEMO_ACCOUNTS.map(([demoEmail, description]) => (
              <button
                key={demoEmail}
                type="button"
                onClick={() => fillDemo(demoEmail)}
                className="flex items-baseline justify-between gap-2 rounded-[var(--radius-sm)] px-2 py-1 text-left text-[12px] transition-colors hover:bg-[var(--surface-glass-strong)]"
              >
                <span className="mono text-[var(--accent)]">{demoEmail}</span>
                <span className="text-[var(--text-muted)]">{description}</span>
              </button>
            ))}
          </div>
          <p className="mono mt-2 text-[11px] text-[var(--text-muted)]">
            heslo: AirshipDemo2026!
          </p>
        </div>
      </div>
    </div>
  );
}
